/* Command Media Tag Resolver */
/* (C) 2024 - 2025, by Drew Anderson  - Cloud Media Works, LLC - All Rights Reserved */

export type ResolvedCommand = {
  type: 'lang'
  lang: string
  key: string
}

/* ── Lang normalizer ───────────────────────────────────────────────────── */

function normalizeCommandLang(lang: string): string {
  const s = String(lang || '').trim()
  if (!s) return 'English'
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

/* ── Primary resolver ──────────────────────────────────────────────────── */

/**
 * Scans the bot's media object for entries with `type: "command"` and
 * returns the first one whose key or alias matches the input text.
 *
 * Matching rules:
 *   - Case-insensitive
 *   - Exact match OR input contains the alias/key as a substring
 *   - Only fires on user-originated text — never call with assistant output
 */
/**
 * Does `haystack` contain `needle` as a WHOLE KEY (token-boundary aligned)?
 *
 * ⚠️ THE RULED FIX (substring hijack → whole-key matching), landed 2026-08-10 under GB's
 * ruling Q1 = option (b): BOTH halves of this matcher move together.
 *
 * This function previously used bare `.includes()`, so any utterance CONTAINING a registered
 * key matched it. With a short key like "es" that captures ordinary sentences — "I have
 * questions" — and the user's actual question is never answered. That is the live
 * SAY-substring-hijack class.
 *
 * Whole-KEY, not whole-STRING: "please switch to Spanish mode" must still match "Spanish mode".
 *
 * ⚠️ TWIN IMPLEMENTATION — keep in lockstep with
 * `relay-server/lib/voice-commands/language.js`. The relay copy exists only to win the
 * session.update race against this one on the SAME turn; the two drifting IS the defect they
 * were written to avoid. Both are pinned by ONE shared table:
 *   relay-server/test/command-matcher.fixtures.json
 */
export function containsWholeKey(haystack: string, needle: string): boolean {
  if (!haystack || !needle) return false
  let from = 0
  for (;;) {
    const i = haystack.indexOf(needle, from)
    if (i === -1) return false
    const before = i === 0 ? '' : haystack[i - 1]
    const afterIdx = i + needle.length
    const after = afterIdx >= haystack.length ? '' : haystack[afterIdx]
    const isBoundary = (c: string) => c === '' || !/[a-z0-9]/i.test(c)
    if (isBoundary(before) && isBoundary(after)) return true
    from = i + 1
  }
}

export function resolveCommandFromInput(
  text: string,
  bot: unknown,
): ResolvedCommand | null {
  if (!text || !bot || typeof bot !== 'object') return null

  const media = (bot as any)?.media ?? {}
  if (!media || typeof media !== 'object') return null

  const normalized = text.trim().toLowerCase()

  for (const [key, tag] of Object.entries(media)) {
    const t = tag as any
    if (t?.type !== 'command') continue
    if (!t?.lang) continue

    const alias = String(t.alias || key).trim().toLowerCase()
    const keyNorm = key.trim().toLowerCase()

    if (
      normalized === alias ||
      normalized === keyNorm ||
      containsWholeKey(normalized, alias) ||
      containsWholeKey(normalized, keyNorm)
    ) {
      return { type: 'lang', lang: normalizeCommandLang(t.lang), key }
    }
  }

  return null
}
